// OSGB Frontend build: v1.3.26 - Coordinator Accounting Access
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BranchProvider } from './contexts/BranchContext';
import { FieldAuthProvider } from './contexts/FieldAuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import MainLayout from './components/MainLayout';
import './App.css';
import './accessibility.css';
// Ana pakette yüklensin; lazy alt chunk ile ayrı WorkplaceDetailModal_css chunk'ı dev'de sık ChunkLoadError veriyordu
import './components/WorkplaceDetailModal.css';

// Lazy load components
const WelcomePage = React.lazy(() => import('./components/WelcomePage'));
const StatisticsPage = React.lazy(() => import('./components/StatisticsPage'));
const Dashboard = React.lazy(() => import('./components/Dashboard'));
const ExpertCardPage = React.lazy(() => import('./components/ExpertCardPage'));
const DoctorCardPage = React.lazy(() => import('./components/DoctorCardPage'));
const DspCardPage = React.lazy(() => import('./components/DspCardPage'));
const WorkplacePage = React.lazy(() => import('./components/WorkplacePage'));
const VisitsPage = React.lazy(() => import('./components/VisitsPage'));
const IsgKatipPage = React.lazy(() => import('./components/IsgKatipPage'));
const AccountingPage = React.lazy(() => import('./components/AccountingPage'));
const LoginPage = React.lazy(() => import('./components/LoginPage'));
const OrganizationRegisterPage = React.lazy(() => import('./components/OrganizationRegisterPage'));
const SubscriptionPage = React.lazy(() => import('./components/SubscriptionPage'));
const AdminPanel = React.lazy(() => import('./components/AdminPanel'));
const ProfilePage = React.lazy(() => import('./components/ProfilePage'));
const OrganizationSettings = React.lazy(() => import('./components/OrganizationSettings'));
const ArchivePage = React.lazy(() => import('./components/ArchivePage'));
const ForgotPasswordPage = React.lazy(() => import('./components/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./components/ResetPasswordPage'));
const VerifyEmailPage = React.lazy(() => import('./components/VerifyEmailPage'));
const KVKKPage = React.lazy(() => import('./components/KVKKPage'));
const AboutPage = React.lazy(() => import('./components/AboutPage'));
const ContactPage = React.lazy(() => import('./components/ContactPage'));
import RoleGuard from './components/RoleGuard';
import ErrorBoundary from './components/ErrorBoundary';

// Client Portal Components
const ClientLayout = React.lazy(() => import('./components/ClientLayout'));
const ClientDashboard = React.lazy(() => import('./components/ClientDashboard'));
const ClientDocumentsPage = React.lazy(() => import('./components/ClientDocumentsPage'));
const EmployeeCardPage = React.lazy(() => import('./components/EmployeeCardPage'));
const ClientSettingsPage = React.lazy(() => import('./components/ClientSettingsPage'));
const ClientActivityPage = React.lazy(() => import('./components/ClientActivityPage'));
const AdminMobileDashboard = React.lazy(() => import('./pages/field/AdminMobileDashboard'));
const CoordinatorMobileDashboard = React.lazy(() => import('./pages/field/CoordinatorMobileDashboard'));
const ArchiveMobileDashboard = React.lazy(() => import('./pages/field/ArchiveMobileDashboard'));
const FieldLoginPage = React.lazy(() => import('./pages/field/FieldLoginPage'));
const FieldLayout = React.lazy(() => import('./components/FieldLayout'));
const FieldDashboard = React.lazy(() => import('./pages/field/FieldDashboard'));
const FieldWorkplaceDetail = React.lazy(() => import('./pages/field/FieldWorkplaceDetail'));
const FieldNotifications = React.lazy(() => import('./pages/field/FieldNotifications'));
const WebFieldLoginPage = React.lazy(() => import('./pages/field/WebFieldLoginPage'));
const WebFieldLayout = React.lazy(() => import('./components/WebFieldLayout'));
const WebFieldDashboard = React.lazy(() => import('./pages/field/WebFieldDashboard'));
const WebFieldWorkplaceDetail = React.lazy(() => import('./pages/field/WebFieldWorkplaceDetail'));
const WebFieldVisitsPage = React.lazy(() => import('./pages/field/WebFieldVisitsPage'));
const RiskAnalysisPage = React.lazy(() => import('./pages/field/RiskAnalysisPage'));
const AnnualWorkPlanPage = React.lazy(() => import('./pages/field/AnnualWorkPlanPage'));
const AnnualTrainingPlanPage = React.lazy(() => import('./pages/field/AnnualTrainingPlanPage'));
const EmergencyActionPlanPage = React.lazy(() => import('./pages/field/EmergencyActionPlanPage'));
const WebFieldArchivePage = React.lazy(() => import('./pages/field/WebFieldArchivePage'));
const WebFieldDocumentsPage = React.lazy(() => import('./pages/field/WebFieldDocumentsPage'));
const WebFieldCertificatesPage = React.lazy(() => import('./pages/field/WebFieldCertificatesPage'));
const EvacuationPlanPage = React.lazy(() => import('./pages/field/EvacuationPlanPage'));
const WebFieldChangePasswordPage = React.lazy(() => import('./pages/field/WebFieldChangePasswordPage'));
const WebFieldSettingsFormatPage = React.lazy(() => import('./pages/field/WebFieldSettingsFormatPage'));

// Admin check wrapper
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.role !== 'admin') return <Navigate to="/" />;
  return <MainLayout>{children}</MainLayout>;
};

// Client check wrapper
const ClientRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.role !== 'client') return <Navigate to="/" />;
  return <ClientLayout>{children}</ClientLayout>;
};

// Home redirect wrapper
const HomeRoute = () => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  
  // Security: Expert/Doctor users MUST NOT access admin dashboard
  // They must use /field or /web/field login pages
  if (user?.role === 'expert' || user?.role === 'doctor') {
    return <Navigate to="/web/field/login" replace />;
  }
  
  if (user?.role === 'hr' || user?.role === 'accounting' || user?.role === 'arsiv' || user?.role === 'coordinator' || user?.role === 'auditor') {
    return <Navigate to="/dashboard" replace />;
  }
  return <MainLayout><WelcomePage /></MainLayout>;
};


import SubscriptionExpiredOverlay from './components/SubscriptionExpiredOverlay';

const SubscriptionGate = ({ children }: { children: React.ReactNode }) => {
  const { pathname } = useLocation();
  const publicPaths = ['/login', '/field/login', '/web/field/login', '/register-organization', '/subscription', '/forgot-password', '/reset-password', '/verify-email', '/about', '/contact'];
  const isPublicPath = publicPaths.some(path => pathname === path || pathname.startsWith(path));

  return (
    <>
      {!isPublicPath && <SubscriptionExpiredOverlay />}
      {children}
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <BranchProvider>
        <FieldAuthProvider>
          <SubscriptionProvider>
          <Router>
            <ErrorBoundary>
              <SubscriptionGate>
                <div className="app">
                <Routes>
                  {/* Field App Routes (PWA) */}
                  <Route path="/field/login" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <FieldLoginPage />
                    </Suspense>
                  } />

                  {/* Field Protected Routes */}
                  <Route path="/field" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <FieldLayout />
                    </Suspense>
                  }>
                    <Route path="dashboard" element={<FieldDashboard />} />
                    <Route path="workplace/:id" element={<FieldWorkplaceDetail />} />
                    <Route path="notifications" element={<FieldNotifications />} />
                    <Route path="admin" element={<AdminMobileDashboard />} />
                    <Route path="coordinator" element={<CoordinatorMobileDashboard />} />
                    <Route path="archive" element={<ArchiveMobileDashboard />} />
                  </Route>

                  {/* Web Field Dashboard Routes (Desktop) */}
                  <Route path="/web/field/login" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <WebFieldLoginPage />
                    </Suspense>
                  } />
                  <Route path="/web/field" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <WebFieldLayout />
                    </Suspense>
                  }>
                    <Route path="dashboard" element={<WebFieldDashboard />} />
                    {/* Map /workplaces routes to WebFieldDashboard so it reads listFilter from URL */}
                    <Route path="workplaces">
                        <Route index element={<WebFieldDashboard />} />
                        <Route path="assigned" element={<WebFieldDashboard />} />
                        <Route path="tracking" element={<WebFieldDashboard />} />
                        <Route path="pending" element={<WebFieldDashboard />} />
                    </Route>
                    <Route path="visits" element={<WebFieldVisitsPage />} />
                    <Route path="archive" element={<WebFieldArchivePage />} />
                    <Route path="workplace/:id" element={<WebFieldWorkplaceDetail />} />
                    <Route path="documents" element={<WebFieldDocumentsPage />} />
                    <Route path="documents/risk" element={<RiskAnalysisPage />} />
                    <Route path="documents/emergency" element={<EmergencyActionPlanPage />} />
                    <Route path="documents/evacuation-plan" element={<EvacuationPlanPage />} />
                    <Route path="documents/annual" element={<AnnualWorkPlanPage />} />
                    <Route path="documents/annual-work-plan" element={<AnnualWorkPlanPage />} />
                    <Route path="documents/annual-training-plan" element={<AnnualTrainingPlanPage />} />
                    <Route path="documents/certificates" element={<WebFieldCertificatesPage />} />
                    <Route path="documents/drill" element={<WebFieldDocumentsPage />} />
                    <Route path="settings/password" element={<WebFieldChangePasswordPage />} />
                    <Route path="settings/format" element={<WebFieldSettingsFormatPage />} />
                  </Route>

                  {/* Public Routes */}
                  <Route path="/login" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <LoginPage />
                    </Suspense>
                  } />
                  <Route path="/register-organization" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <OrganizationRegisterPage />
                    </Suspense>
                  } />
                  <Route path="/subscription" element={
                    <RoleGuard allowedRoles={['admin', 'osgb_admin']}>
                      <Suspense fallback={<div>Yükleniyor...</div>}>
                        <SubscriptionPage />
                      </Suspense>
                    </RoleGuard>
                  } />
                  <Route path="/forgot-password" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <ForgotPasswordPage />
                    </Suspense>
                  } />
                  <Route path="/reset-password" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <ResetPasswordPage />
                    </Suspense>
                  } />
                  <Route path="/verify-email" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <VerifyEmailPage />
                    </Suspense>
                  } />
                  <Route path="/kvkk" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <KVKKPage />
                    </Suspense>
                  } />
                  <Route path="/about" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <AboutPage />
                    </Suspense>
                  } />
                  <Route path="/contact" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <ContactPage />
                    </Suspense>
                  } />
                  <Route path="/kvkk" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <KVKKPage />
                    </Suspense>
                  } />

                  {/* Protected Routes (Authenticated) */}
                  <Route path="/" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <HomeRoute />
                    </Suspense>
                  } />
                  <Route path="/dashboard" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <MainLayout><Dashboard /></MainLayout>
                    </Suspense>
                  } />

                  {/* Statistics: Accounting, HR, Arsiv, OSGB Admin, Coordinator, Auditor */}
                  <Route path="/statistics" element={
                    <RoleGuard allowedRoles={['accounting', 'hr', 'arsiv', 'coordinator', 'auditor']}>
                      <MainLayout><StatisticsPage /></MainLayout>
                    </RoleGuard>
                  } />

                  {/* Personnel: HR, OSGB Admin, Coordinator, Auditor */}
                  <Route path="/experts" element={
                    <RoleGuard allowedRoles={['hr', 'accounting', 'coordinator', 'auditor']}>
                      <MainLayout><ExpertCardPage /></MainLayout>
                    </RoleGuard>
                  } />
                  <Route path="/doctors" element={
                    <RoleGuard allowedRoles={['hr', 'accounting', 'coordinator', 'auditor']}>
                      <MainLayout><DoctorCardPage /></MainLayout>
                    </RoleGuard>
                  } />
                  <Route path="/dsps" element={
                    <RoleGuard allowedRoles={['hr', 'accounting', 'coordinator', 'auditor']}>
                      <MainLayout><DspCardPage /></MainLayout>
                    </RoleGuard>
                  } />

                  {/* Workplaces: HR, Accounting (View Only), OSGB Admin, Coordinator, Auditor */}
                  <Route path="/workplaces" element={
                    <RoleGuard allowedRoles={['hr', 'accounting', 'coordinator', 'auditor']}>
                      <MainLayout><WorkplacePage /></MainLayout>
                    </RoleGuard>
                  } />

                  {/* Visits: OSGB Admin, Coordinator, Auditor */}
                  <Route path="/visits" element={
                    <RoleGuard allowedRoles={['coordinator', 'auditor']}>
                      <MainLayout><VisitsPage /></MainLayout>
                    </RoleGuard>
                  } />

                  {/* Archive: HR, OSGB Admin, Coordinator, Auditor */}
                  <Route path="/archive" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <RoleGuard allowedRoles={['hr', 'accounting', 'arsiv', 'coordinator', 'auditor']}>
                        <MainLayout><ArchivePage /></MainLayout>
                      </RoleGuard>
                    </Suspense>
                  } />

                  {/* ISG Katip: HR, OSGB Admin */}
                  <Route path="/isg-katip" element={
                    <RoleGuard allowedRoles={['hr', 'accounting']}>
                      <MainLayout><IsgKatipPage /></MainLayout>
                    </RoleGuard>
                  } />

                  {/* Accounting Page: Accounting, HR, OSGB Admin, Coordinator, Auditor */}
                  <Route path="/accounting" element={
                    <RoleGuard allowedRoles={['accounting', 'hr', 'coordinator', 'auditor', 'osgb_admin', 'admin']}>
                      <MainLayout><AccountingPage /></MainLayout>
                    </RoleGuard>
                  } />

                  <Route path="/profile" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <MainLayout><ProfilePage /></MainLayout>
                    </Suspense>
                  } />

                  {/* Settings: OSGB Admin Only */}
                  <Route path="/settings" element={
                    <RoleGuard allowedRoles={['osgb_admin', 'admin']}>
                      <MainLayout><OrganizationSettings /></MainLayout>
                    </RoleGuard>
                  } />

                  {/* Admin Route (Super Admin / Master Admin) */}
                  <Route path="/admin" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <AdminRoute><AdminPanel /></AdminRoute>
                    </Suspense>
                  } />

                  {/* Client Portal Routes */}
                  <Route path="/client/dashboard" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <ClientRoute><ClientDashboard /></ClientRoute>
                    </Suspense>
                  } />
                  <Route path="/client/documents" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <ClientRoute><ClientDocumentsPage /></ClientRoute>
                    </Suspense>
                  } />
                  <Route path="/client/employees" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <ClientRoute><EmployeeCardPage /></ClientRoute>
                    </Suspense>
                  } />
                  <Route path="/client/activities" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <ClientRoute><ClientActivityPage /></ClientRoute>
                    </Suspense>
                  } />
                  <Route path="/client/settings" element={
                    <Suspense fallback={<div>Yükleniyor...</div>}>
                      <ClientRoute><ClientSettingsPage /></ClientRoute>
                    </Suspense>
                  } />
                </Routes>
              </div>
            </SubscriptionGate>
            </ErrorBoundary>
          </Router>
        </SubscriptionProvider>
      </FieldAuthProvider>
    </BranchProvider>
  </AuthProvider>
  );
}

export default App;
